import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Map route paths to permission keys
const ROUTE_PERMISSIONS: Record<string, string> = {
  '/': 'dashboard',
  '/analytics': 'analytics',
  '/orders': 'orders',
  '/dispatch': 'dispatch',
  '/inventory': 'inventory',
  '/inventory-analytics': 'inventory_analytics',
  '/reorder': 'reorder',
  '/rto': 'rto',
  '/design-mappings': 'design_mappings',
  '/expenses': 'expenses',
  '/pl': 'pl',
  '/tax': 'tax',
  '/invoices': 'invoices',
  '/proforma': 'proforma',
  '/settings': 'settings',
  '/users': 'settings',
};

// All routes in order of priority for redirect
const ALL_ROUTES = [
  { path: '/', perm: 'dashboard' },
  { path: '/analytics', perm: 'analytics' },
  { path: '/orders', perm: 'orders' },
  { path: '/dispatch', perm: 'dispatch' },
  { path: '/inventory', perm: 'inventory' },
  { path: '/inventory-analytics', perm: 'inventory_analytics' },
  { path: '/reorder', perm: 'reorder' },
  { path: '/rto', perm: 'rto' },
  { path: '/design-mappings', perm: 'design_mappings' },
  { path: '/expenses', perm: 'expenses' },
  { path: '/pl', perm: 'pl' },
  { path: '/tax', perm: 'tax' },
  { path: '/invoices', perm: 'invoices' },
  { path: '/proforma', perm: 'proforma' },
  { path: '/settings', perm: 'settings' },
];

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#080810', color: '#6e8efb', fontFamily: 'sans-serif' }}>
      Loading...
    </div>
  );

  // Not logged in → go to login
  if (!user) return <Navigate to="/login" />;

  // Check if user has permission for this route
  const requiredPerm = ROUTE_PERMISSIONS[location.pathname];

  if (requiredPerm && !hasPermission(requiredPerm)) {
    // Find the first page they DO have access to
    const firstAllowed = ALL_ROUTES.find(r => hasPermission(r.perm));
    if (firstAllowed) {
      return <Navigate to={firstAllowed.path} replace />;
    }
    // No pages at all — show error
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        height: '100vh', background: '#080810', color: '#ef5a6f', fontFamily: 'sans-serif', textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🚫</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Access Denied</div>
        <div style={{ fontSize: 14, color: '#7a7d9e' }}>You don't have permission to view this page.</div>
        <div style={{ fontSize: 13, color: '#4d506b', marginTop: 4 }}>Contact your admin for access.</div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
